/**
 * MUTAGIM.SHOP — Search Autocomplete (Phase 6 + Phase 7)
 * Barre de recherche avec autocomplétion, historique et recherche par image
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, Loader2, X, Camera, History, TrendingUp, ImageIcon } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useCurrency } from '../../contexts/CurrencyContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hook debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

export function SearchAutocomplete({ className, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const lang = i18n.language;
  const isRTL = i18n.dir() === 'rtl';
  
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showHistory, setShowHistory] = useState(false);
  
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Debounce de 300ms
  const debouncedQuery = useDebounce(query, 300);
  
  // Charger l'historique et les recherches populaires
  const loadHistoryAndPopular = useCallback(async () => {
    try {
      const token = localStorage.getItem('customer_token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [historyRes, popularRes] = await Promise.all([
        axios.get(`${API_URL}/api/search/history`, { headers }).catch(() => ({ data: { history: [] } })),
        axios.get(`${API_URL}/api/search/popular?limit=5`).catch(() => ({ data: { popular: [] } }))
      ]);
      
      setHistory(historyRes.data.history || []);
      setPopular(popularRes.data.popular || []);
    } catch (err) {
      console.error('Failed to load history/popular:', err);
    }
  }, []);
  
  useEffect(() => {
    loadHistoryAndPopular();
  }, [loadHistoryAndPopular]);
  
  // Charger les suggestions
  const loadSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      setShowHistory(true);
      return;
    }
    
    setLoading(true);
    setShowHistory(false);
    try {
      const response = await axios.get(`${API_URL}/api/storefront/search/suggestions`, {
        params: { q: searchQuery, limit: 5 }
      });
      
      setSuggestions(response.data.suggestions || []);
      setIsOpen(true);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Suggestions error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (debouncedQuery) {
      loadSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
      setShowHistory(true);
    }
  }, [debouncedQuery, loadSuggestions]);
  
  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Navigation clavier
  const handleKeyDown = (e) => {
    const items = showHistory ? [...history, ...popular] : suggestions;
    
    if (!isOpen || items.length === 0) {
      if (e.key === 'Enter' && query) {
        handleSearch();
      }
      return;
    }
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < items.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : items.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && items[selectedIndex]) {
          if (showHistory) {
            // C'est un terme d'historique ou populaire
            setQuery(items[selectedIndex].query);
            handleSearch(items[selectedIndex].query);
          } else if (suggestions[selectedIndex]) {
            handleSelectSuggestion(suggestions[selectedIndex]);
          }
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };
  
  // Lancer la recherche
  const handleSearch = async (searchQuery = query) => {
    if (searchQuery.trim()) {
      // Logger la recherche
      try {
        const token = localStorage.getItem('customer_token') || localStorage.getItem('admin_token');
        await axios.post(`${API_URL}/api/search/log`, null, {
          params: { query: searchQuery.trim(), results_count: 0 },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      } catch (err) {
        // Ignorer les erreurs de logging
      }
      
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsOpen(false);
      setQuery('');
      onClose?.();
    }
  };
  
  // Sélectionner une suggestion
  const handleSelectSuggestion = (suggestion) => {
    navigate(`/product/${suggestion.slug}`);
    setIsOpen(false);
    setQuery('');
    onClose?.();
  };
  
  // Recherche par image
  const handleImageSearch = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      toast.error(t('search.invalidImage', 'Veuillez sélectionner une image'));
      return;
    }
    
    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('search.imageTooLarge', 'Image trop volumineuse (max 5MB)'));
      return;
    }
    
    setImageLoading(true);
    toast.info(t('search.analyzingImage', 'Analyse de l\'image en cours...'));
    
    try {
      // Convertir en base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        try {
          const response = await axios.post(`${API_URL}/api/search/by-image-base64`, {
            image_base64: base64
          });
          
          if (response.data.success && response.data.search_query) {
            toast.success(t('search.imageAnalyzed', 'Image analysée ! Recherche: {{query}}', {
              query: response.data.search_query
            }));
            
            // Naviguer vers les résultats
            navigate(`/search?q=${encodeURIComponent(response.data.search_query)}&image_search=true`);
            onClose?.();
          } else {
            toast.error(t('search.noResultsImage', 'Aucun produit similaire trouvé'));
          }
        } catch (err) {
          console.error('Image search error:', err);
          toast.error(t('search.imageSearchError', 'Erreur lors de l\'analyse'));
        }
      };
      
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error(t('search.imageReadError', 'Erreur lors de la lecture de l\'image'));
    } finally {
      setImageLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Effacer l'historique
  const handleClearHistory = async () => {
    try {
      const token = localStorage.getItem('customer_token') || localStorage.getItem('admin_token');
      if (token) {
        await axios.delete(`${API_URL}/api/search/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory([]);
        toast.success(t('search.historyCleared', 'Historique effacé'));
      }
    } catch (err) {
      toast.error(t('search.historyClearError', 'Erreur'));
    }
  };
  
  // Effacer la recherche
  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowHistory(true);
    inputRef.current?.focus();
  };
  
  // Highlight du terme recherché dans le texte
  const highlightText = (text, highlight) => {
    if (!highlight || !text) return text;
    
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-mutagim-red/30 text-white rounded px-0.5">
          {part}
        </mark>
      ) : part
    );
  };
  
  // Formater l'URL de l'image
  const getImageUrl = (img) => {
    if (!img) return '/placeholder-product.jpg';
    if (img.startsWith('/api/')) return `${API_URL}${img}`;
    return img;
  };
  
  // Déterminer si on doit afficher le dropdown
  const shouldShowDropdown = isOpen && (
    (showHistory && (history.length > 0 || popular.length > 0)) ||
    (!showHistory && suggestions.length > 0)
  );

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Input */}
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <Search className={cn(
            "absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400",
            isRTL ? "right-3" : "left-3"
          )} />
          
          <Input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={t('header.searchPlaceholder', 'Rechercher...')}
            className={cn(
              "w-full bg-gray-800 border-gray-700 text-white placeholder:text-gray-400",
              "focus:border-mutagim-red focus:ring-mutagim-red/20",
              isRTL ? "pr-10 pl-10" : "pl-10 pr-10"
            )}
            data-testid="search-autocomplete-input"
          />
          
          {/* Loading / Clear button */}
          <div className={cn(
            "absolute top-1/2 -translate-y-1/2",
            isRTL ? "left-2" : "right-2"
          )}>
            {loading ? (
              <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
            ) : query && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-gray-400 hover:text-white"
                onClick={handleClear}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Bouton recherche par image */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSearch}
        />
        <Button
          variant="outline"
          size="icon"
          className="border-gray-700 text-gray-400 hover:text-white hover:border-mutagim-red"
          onClick={() => fileInputRef.current?.click()}
          disabled={imageLoading}
          title={t('search.searchByImage', 'Rechercher par image')}
          data-testid="image-search-button"
        >
          {imageLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Camera className="h-4 w-4" />
          )}
        </Button>
      </div>
      
      {/* Dropdown */}
      {shouldShowDropdown && (
        <div 
          className={cn(
            "absolute top-full mt-2 w-full bg-gray-900 border border-gray-700 rounded-lg",
            "shadow-xl shadow-black/50 z-50 overflow-hidden",
            isRTL ? "right-0" : "left-0"
          )}
          data-testid="search-dropdown"
        >
          {/* Historique et Populaires */}
          {showHistory && !query && (
            <>
              {/* Historique */}
              {history.length > 0 && (
                <div className="p-2">
                  <div className="flex items-center justify-between px-2 mb-2">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <History className="w-3 h-3" />
                      {t('search.recentSearches', 'Recherches récentes')}
                    </span>
                    <button
                      className="text-xs text-gray-500 hover:text-mutagim-red"
                      onClick={handleClearHistory}
                    >
                      {t('search.clear', 'Effacer')}
                    </button>
                  </div>
                  {history.map((item, index) => (
                    <button
                      key={`h-${index}`}
                      className={cn(
                        "w-full flex items-center gap-2 p-2 text-left rounded transition-colors",
                        "hover:bg-gray-800 text-gray-300",
                        selectedIndex === index && "bg-gray-800"
                      )}
                      onClick={() => {
                        setQuery(item.query);
                        handleSearch(item.query);
                      }}
                    >
                      <History className="w-4 h-4 text-gray-500" />
                      <span>{item.query}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Populaires */}
              {popular.length > 0 && (
                <div className="p-2 border-t border-gray-800">
                  <span className="text-xs text-gray-400 flex items-center gap-1 px-2 mb-2">
                    <TrendingUp className="w-3 h-3" />
                    {t('search.popularSearches', 'Recherches populaires')}
                  </span>
                  {popular.map((item, index) => (
                    <button
                      key={`p-${index}`}
                      className={cn(
                        "w-full flex items-center gap-2 p-2 text-left rounded transition-colors",
                        "hover:bg-gray-800 text-gray-300",
                        selectedIndex === history.length + index && "bg-gray-800"
                      )}
                      onClick={() => {
                        setQuery(item.query);
                        handleSearch(item.query);
                      }}
                    >
                      <TrendingUp className="w-4 h-4 text-orange-400" />
                      <span>{item.query}</span>
                      <span className="text-xs text-gray-500 ms-auto">{item.count}x</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          
          {/* Suggestions de produits */}
          {!showHistory && suggestions.length > 0 && (
            <>
              {suggestions.map((suggestion, index) => {
                const name = suggestion.name?.[lang] || suggestion.name?.fr || '';
                
                return (
                  <button
                    key={suggestion.id}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 text-left transition-colors",
                      "hover:bg-gray-800",
                      index === selectedIndex && "bg-gray-800"
                    )}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    data-testid={`suggestion-${index}`}
                  >
                    {/* Image produit */}
                    <div className="w-12 h-12 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                      <img
                        src={getImageUrl(suggestion.image)}
                        alt={name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Infos produit */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">
                        {highlightText(name, debouncedQuery)}
                      </p>
                      <p className="text-mutagim-red text-sm font-semibold">
                        {formatPrice(suggestion.price)}
                      </p>
                    </div>
                  </button>
                );
              })}
              
              {/* Voir tous les résultats */}
              <button
                className="w-full p-3 text-center text-mutagim-red hover:bg-gray-800 transition-colors border-t border-gray-800"
                onClick={() => handleSearch()}
              >
                {t('search.seeAllResults', 'Voir tous les résultats pour')} "{query}"
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchAutocomplete;
